import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { isNil } from 'lodash';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { OrganizationUnitVisionService } from 'src/api/customer/organization-unit-vision/organization-unit-vision.service';
import { AiAdjustmentProvider } from 'src/providers/ai-adjustment.provider';
import { I18nToastProvider } from 'src/providers/i18n-toast.provider';

/**
 * The organization's pedagogical vision, used as an optional steering input
 * for AI activity adjustments.
 *
 * Editable by organization management, not just DoenKids: an organization must
 * be able to write its own vision. Deliberately not gated on the AI enablement
 * flags either, so a vision can be prepared before AI is switched on.
 */
@Component({
  selector: 'app-organization-unit-vision-form',
  templateUrl: './organization-unit-vision-form.component.html',
  styleUrls: ['./organization-unit-vision-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OrganizationUnitVisionFormComponent implements OnChanges {
  /** The organization unit the user is working in. The vision is stored on its customer */
  @Input() organizationUnitId: number;

  /** False when the user may read the vision but not change it */
  @Input() canEdit = false;

  public visionControl = new FormControl<string>('');

  public loading = false;

  public saving = false;

  /** Guards against an older fetch landing after a newer one */
  private fetchGeneration = 0;

  /** True when a vision is stored, which is what makes clearing it possible */
  public hasStoredVision = false;

  /** True when this organization unit has no customer to hold a vision */
  public notApplicable = false;

  constructor(
    private organizationUnitVisionService: OrganizationUnitVisionService,
    private $aiAdjustment: AiAdjustmentProvider,
    private $i18nToastProvider: I18nToastProvider,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (!isNil(changes.organizationUnitId) && !isNil(this.organizationUnitId)) {
      this.fetch();
    }

    if (!isNil(changes.canEdit)) {
      if (this.canEdit) {
        this.visionControl.enable({ emitEvent: false });
      } else {
        this.visionControl.disable({ emitEvent: false });
      }
    }
  }

  /** The vision is only clearable once something is stored. */
  public get canClear(): boolean {
    return this.canEdit && this.hasStoredVision && !this.saving && !this.loading;
  }

  public get canSave(): boolean {
    return this.canEdit
      && !this.saving
      && !this.loading
      && this.visionControl.dirty
      && `${this.visionControl.value ?? ''}`.trim().length > 0;
  }

  public async fetch(): Promise<void> {
    const generation = ++this.fetchGeneration;
    const organizationUnitId = this.organizationUnitId;

    this.loading = true;
    this.notApplicable = false;

    // The organisation can change under a LIVE component: the details page
    // reassigns its id rather than being recreated, so without this the form
    // goes on showing the previous organisation's vision while the new one
    // loads — and Clear would then archive the NEW organisation's vision while
    // the old one is on screen.
    //
    this.hasStoredVision = false;
    this.visionControl.setValue('', { emitEvent: false });
    this.visionControl.markAsPristine();
    this.cdr.markForCheck();

    try {
      const vision = await this.organizationUnitVisionService.fetch(organizationUnitId);

      if (generation !== this.fetchGeneration) {
        return;
      }

      this.hasStoredVision = !isNil(vision);
      this.visionControl.setValue(vision?.vision ?? '', { emitEvent: false });
      this.visionControl.markAsPristine();
    } catch (error) {
      if (generation !== this.fetchGeneration) {
        return;
      }

      // A unit with no customer ancestor has nowhere to store a vision. That
      // is a property of where the user is standing, not a failure worth
      // shouting about, so the section simply says it does not apply.
      //
      if ((error as { status?: number })?.status === 404) {
        this.notApplicable = true;
      } else {
        this.$i18nToastProvider.error(_('organization_unit.vision.fetch_failed'));
      }
    }

    if (generation !== this.fetchGeneration) {
      return;
    }

    this.loading = false;
    this.cdr.markForCheck();
  }

  public async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }

    this.saving = true;
    this.cdr.markForCheck();

    try {
      await this.organizationUnitVisionService.update(this.organizationUnitId, `${this.visionControl.value}`.trim());

      this.hasStoredVision = true;
      this.visionControl.markAsPristine();
      this.$i18nToastProvider.success(_('organization_unit.vision.save_success'));

      // A dock that was offering a disabled vision toggle can now offer it.
      //
      await this.$aiAdjustment.reload();
    } catch (error) {
      this.$i18nToastProvider.error(_('organization_unit.vision.save_failed'));
    }

    this.saving = false;
    this.cdr.markForCheck();
  }

  /**
   * Clears the stored vision.
   *
   * A separate call rather than saving an empty string: the API rejects an
   * empty vision, and archiving is how a vision is removed.
   */
  public async clear(): Promise<void> {
    if (!this.canClear) {
      return;
    }

    this.saving = true;
    this.cdr.markForCheck();

    try {
      await this.organizationUnitVisionService.archive(this.organizationUnitId);

      this.hasStoredVision = false;
      this.visionControl.setValue('', { emitEvent: false });
      this.visionControl.markAsPristine();
      this.$i18nToastProvider.success(_('organization_unit.vision.clear_success'));

      await this.$aiAdjustment.reload();
    } catch (error) {
      this.$i18nToastProvider.error(_('organization_unit.vision.clear_failed'));
    }

    this.saving = false;
    this.cdr.markForCheck();
  }
}
